"use client";

import { useTheaterOverlayFrozen } from "@/components/feature/theater-overlay-freeze";
import { useEffect, useRef, useState } from "react";

// Animation constants - defined at module level to prevent redefinition
const MIN_SNOWFLAKE_SIZE = 32;
const SNOWFLAKE_SIZE_RANGE = 43;
const MIN_ANGLE = 15;
const ANGLE_RANGE = 30;
const MIN_SWAY_DISTANCE = 25;
const SWAY_DISTANCE_RANGE = 35;
const BASE_FALL_DURATION = 10;
const VELOCITY_MULTIPLIER = 3;
const INITIAL_OPACITY = 0.8;
const MAX_CONCURRENT_SNOWFLAKES = 12;

/**
 * WinterBackground
 *
 * Background decorations for Winter theme.
 * Part of the decoration system - controlled via NEXT_PUBLIC_ACTIVE_DECORATIONS.
 *
 * Animated falling snowflakes that sway side-to-side and rotate as they fall,
 * mimicking natural snowflake motion. Uses CSS animations for optimal performance.
 */
export function WinterBackground() {
  // Initialize snowflakeCount to a value within valid range (2-7) to avoid incorrect initial state
  const [snowflakeCount, setSnowflakeCount] = useState(4);
  // Initialize from matchMedia with SSR guard to prevent animation before preference is known
  // Note: This is a "use client" component, so it won't be server-rendered, preventing hydration mismatches
  // The lazy initializer ensures we check the preference synchronously on first render
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  });
  const containerRef = useRef<HTMLDivElement>(null);
  const overlayFrozen = useTheaterOverlayFrozen();

  // Calculate optimal snowflake count based on viewport size
  useEffect(() => {
    const calculateSnowflakeCount = () => {
      if (typeof window === "undefined") return;

      const viewportArea = window.innerWidth * window.innerHeight;
      // Calculate snowflake density based on viewport area
      // Target: ~1 snowflake per 108,000 pixels² (13500 * 8 = 108,000) for balanced visual density
      const calculatedCount = Math.floor(viewportArea / 13500 / 8);
      // Clamp between 2 and 7 snowflakes for optimal performance and visual balance
      const clampedCount = Math.min(7, Math.max(2, calculatedCount));
      setSnowflakeCount(clampedCount);
    };

    // Check for reduced motion preference
    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    setPrefersReducedMotion(mediaQuery.matches);

    const handleReducedMotionChange = (e: MediaQueryListEvent) => {
      setPrefersReducedMotion(e.matches);
    };

    mediaQuery.addEventListener("change", handleReducedMotionChange);

    // Initial calculation
    calculateSnowflakeCount();

    // Recalculate on resize
    window.addEventListener("resize", calculateSnowflakeCount, { passive: true });

    return () => {
      window.removeEventListener("resize", calculateSnowflakeCount);
      mediaQuery.removeEventListener("change", handleReducedMotionChange);
    };
  }, []);

  // Generate snowflake elements
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // Clear existing snowflakes using removeChild for best practice
    while (container.firstChild) {
      container.removeChild(container.firstChild);
    }

    // If reduced motion is preferred, render fewer static snowflakes or none
    if (prefersReducedMotion || overlayFrozen) {
      return;
    }

    // Snowflake image options
    const snowflakeImages = [
      "/images/decorations/winter/snowflake01.webp",
      "/images/decorations/winter/snowflake02.webp",
      "/images/decorations/winter/snowflake03.webp",
    ];

    // Track style element references for efficient cleanup
    const styleElementMap = new Map<string, HTMLStyleElement>();

    // Track snowflake elements for visibility cleanup safety net
    const snowflakeElementMap = new Map<string, HTMLElement>();

    let snowflakeCreationIndex = 0;

    // Function to create a single snowflake
    const createSnowflake = () => {
      // Check concurrent snowflake limit
      if (container.children.length >= MAX_CONCURRENT_SNOWFLAKES) {
        return; // Skip creation if limit reached
      }

      const i = snowflakeCreationIndex++;
      const snowflake = document.createElement("img");
      snowflake.className = "winter-snowflake";

      // Randomly select a snowflake image
      const randomSnowflakeImage =
        snowflakeImages[Math.floor(Math.random() * snowflakeImages.length)];
      snowflake.src = randomSnowflakeImage;
      snowflake.alt = "";
      snowflake.loading = "lazy";
      snowflake.decoding = "async";

      // Algorithm from canvas code:
      // angle = MIN_ANGLE + Math.random() * ANGLE_RANGE (15-45 degrees)
      const angle = MIN_ANGLE + Math.random() * ANGLE_RANGE;
      // v = 20 / angle (velocity - smaller angle = faster fall)
      const velocity = 20 / angle;
      // Convert velocity to duration (faster = shorter duration)
      // Canvas uses continuous update, CSS needs duration
      // Actual range: ~11.3-14.0 seconds (angle 15-45 → velocity 0.44-1.33 → duration 11.3-14.0s)
      const fallDuration = BASE_FALL_DURATION + velocity * VELOCITY_MULTIPLIER;

      // Phase offset for sine wave: i * 0.01 (each snowflake has different phase)
      // Note: Canvas algorithm uses this to create variation, but rotation starts at 0
      const phaseOffset = i * 0.01;

      // Sway distance - horizontal movement creates S-curve
      const swayDistance = MIN_SWAY_DISTANCE + Math.random() * SWAY_DISTANCE_RANGE; // 25-60px sway amplitude
      const initialX = Math.random() * 100; // 0-100vw
      const size = MIN_SNOWFLAKE_SIZE + Math.random() * SNOWFLAKE_SIZE_RANGE; // 32-75px

      // Set initial position and base styles
      // Start at the top, off-screen (slightly above viewport)
      snowflake.style.position = "absolute";
      snowflake.style.left = `${initialX}vw`;
      snowflake.style.top = "-50px"; // Start above viewport so it's not visible until animation starts
      snowflake.style.width = `${size}px`;
      snowflake.style.height = "auto"; // Maintain aspect ratio
      snowflake.style.willChange = "transform";
      snowflake.style.backfaceVisibility = "hidden";
      snowflake.style.transform = "translate3d(0, 0, 0)";
      snowflake.style.pointerEvents = "none";
      snowflake.style.userSelect = "none";

      // Create animation matching canvas algorithm exactly:
      // Rotation: Math.sin(dt * 0.002 * i * 0.01) * angle (sineValue * angle in implementation)
      // The sine wave oscillates continuously, creating smooth back-and-forth rotation
      // We need to approximate this with keyframes
      const animationName = `winter-snowflake-${i}`;

      // Calculate sine wave values at keyframe points
      // Canvas uses: Math.sin(dt * 0.002 * i * 0.01)
      // We'll create keyframes that approximate multiple sine wave cycles
      // Each cycle: sin goes 0 -> 1 -> 0 -> -1 -> 0
      // We'll create 2-3 cycles during the fall
      const styleElement = document.createElement("style");

      // Generate keyframes with proper sine wave values
      // Using array accumulation for better performance than string concatenation
      const keyframeParts: string[] = [`@keyframes ${animationName} {`];

      // Create keyframes at regular intervals approximating sine wave
      // Reduced to 64 keyframes for better performance while maintaining smooth animation
      const numKeyframes = 64; // Balanced between smoothness and performance
      for (let k = 0; k < numKeyframes; k++) {
        const percent = (k / (numKeyframes - 1)) * 100;
        const yPos = (percent / 100) * 110; // 0 to 110vh

        // Calculate sine wave value matching canvas algorithm: Math.sin(dt * 0.002 * i * 0.01)
        // dt * 0.002 * i * 0.01 = time-based sine wave with phase offset per snowflake
        // We approximate this with: sin(2π * cycle * percent/100 + phase)
        const cycles = 2.5;
        const sineValue = Math.sin(
          (2 * Math.PI * cycles * k) / numKeyframes + phaseOffset * Math.PI
        );

        // Horizontal movement: follows sine wave for S-curve
        const horizontalOffset = -sineValue * swayDistance;

        // Rotation: match horizontal movement direction exactly
        // Moving left (sineValue positive, horizontalOffset negative) = clockwise rotation (positive)
        // Moving right (sineValue negative, horizontalOffset positive) = counter-clockwise rotation (negative)
        // Use the same sineValue as horizontal movement to keep them in sync
        const rotation = sineValue * angle;

        // Opacity: maintain full opacity throughout the fall
        const opacity = INITIAL_OPACITY;

        keyframeParts.push(`
          ${percent.toFixed(2)}% {
            transform: translateY(${yPos}vh) translateX(${horizontalOffset.toFixed(2)}px) rotate(${rotation.toFixed(2)}deg);
            opacity: ${opacity.toFixed(2)};
          }`);
      }

      keyframeParts.push("}");
      const keyframes = keyframeParts.join("");
      styleElement.textContent = keyframes;
      document.head.appendChild(styleElement);

      // Store references for efficient cleanup
      styleElementMap.set(animationName, styleElement);
      snowflakeElementMap.set(animationName, snowflake);

      // Apply animation with linear timing, no delay - single cycle (not infinite)
      // Remove snowflake after animation completes to prevent accumulation
      snowflake.style.animation = `${animationName} ${fallDuration}s linear 0s 1`;

      // Remove snowflake and its style when animation ends
      snowflake.addEventListener(
        "animationend",
        () => {
          snowflake.remove();
          // Remove the corresponding style element using stored reference
          const styleElementRef = styleElementMap.get(animationName);
          if (styleElementRef) {
            styleElementRef.remove();
            styleElementMap.delete(animationName);
          }
          snowflakeElementMap.delete(animationName);
        },
        { once: true }
      );

      container.appendChild(snowflake);
    };

    // Create snowflakes continuously at a steady rate
    // Calculate interval: create one snowflake every few seconds for smooth drip
    // Use snowflakeCount to determine rate - more snowflakes = faster creation
    // Interval calculated to create snowflakes at steady rate based on snowflakeCount
    const creationInterval = snowflakeCount > 0 ? (10 * 1000) / snowflakeCount : 2500;

    // Cleanup function for orphaned style elements (safety net)
    const cleanupOrphanedStyles = () => {
      for (const [animationName, snowflakeElement] of snowflakeElementMap) {
        // Check if snowflake is still in DOM
        if (!container.contains(snowflakeElement)) {
          // Snowflake was removed but style element wasn't cleaned up
          const styleElementRef = styleElementMap.get(animationName);
          if (styleElementRef) {
            styleElementRef.remove();
            styleElementMap.delete(animationName);
          }
          snowflakeElementMap.delete(animationName);
        }
      }
    };

    // Add visibility change listener for cleanup safety net
    // Handles cases where animationend doesn't fire (tab switching, navigation)
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Tab hidden - cleanup orphaned styles
        cleanupOrphanedStyles();
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);

    // Create first snowflake immediately
    createSnowflake();

    // Create snowflakes continuously - never stop
    const intervalId = setInterval(() => {
      createSnowflake();
    }, creationInterval);

    // Cleanup
    return () => {
      clearInterval(intervalId);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      // Remove all snowflakes using removeChild for best practice
      while (container.firstChild) {
        container.removeChild(container.firstChild);
      }
      // Remove all style elements using stored references
      for (const [_animationName, styleElement] of styleElementMap) {
        styleElement.remove();
      }
      styleElementMap.clear();
      snowflakeElementMap.clear();
    };
  }, [snowflakeCount, prefersReducedMotion, overlayFrozen]);

  return (
    <>
      {/* Animated snowflakes container */}
      <div
        ref={containerRef}
        aria-hidden="true"
        className="pointer-events-none select-none fixed inset-0 z-[1] overflow-hidden"
      >
        {/* Snowflakes are dynamically inserted here */}
      </div>

      {/* Static decoration images */}
      <div aria-hidden="true" className="pointer-events-none select-none">
        {/* Bottom Left - Slurp */}
        <img
          src="/images/decorations/winter/slurp.webp"
          alt=""
          width={650}
          height={150}
          loading="lazy"
          decoding="async"
          className="fixed bottom-2 left-4 hidden xl:block z-0"
        />

        {/* Bottom Right - Buddy */}
        <img
          src="/images/decorations/winter/buddy.webp"
          alt=""
          width={200}
          height={150}
          loading="lazy"
          decoding="async"
          className="fixed bottom-2 right-4 hidden xl:block z-0"
        />
      </div>
    </>
  );
}
